import {
  Grid,
  List,
  ListSubheader,
  MenuItem,
  TextField,
  Tooltip
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import makeStyles from '@mui/styles/makeStyles';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import * as Actions from 'features/admin/userAccess/action/action';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import * as ValidationUtil from 'utils/ValidationUtil';
import { v4 as uuid } from 'uuid';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    overflow: 'auto',
    maxHeight: 250
  },
  listSection: {
    backgroundColor: 'inherit'
  },
  ul: {
    marginTop: '-10px',
    marginLeft: '20px',
    backgroundColor: 'inherit',
    padding: 0,
    paddingTop: 7
  }
}));

const RoleManagementDialogDetail = (props) => {

  const appCodeMapping = {
    FRONT_OFFICE: 'FO_',
    MASTER_DATA: 'MD_',
    ENIGMA: 'ENIGMA_',
    PLAN_OPTIMIZE: 'PLAN_OPT'
  };

  const classes = useStyles();

  const { permissionList, isDefault, isModified } = props;

  const { control, getValues, watch } = useFormContext();

  const applications = useSelector(
    (state) => state.adminUserAccess.applicationList
  );

  const permissionFilterOptions = createFilterOptions({
    stringify: (option) => `${option.code} ${option.description}`
  });

  const watchPermissions = watch('permissions', null);
  const watchApplications = watch('applicationCode', null);

  useEffect(() => {
    store.dispatch(Actions.fetchApplicationList());
  }, []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Grid
            item
            xs={12}
            style={{ marginBottom: '10px', marginTop: '20px' }}>
            <Controller
              name="code"
              control={control}
              rules={{
                required: "Role code can't be empty.",
                validate: async (value) => {
                  if (value) {
                    if (!/^[A-Z0-9_]*$/.test(value))
                      return 'Only upper case letters, numbers and underscore allowed.';
                    const found = await axios.get(
                      AppConfig.IAM_BACKEND_URL + `/role/role-code?code=${value}`
                    );
                    return ValidationUtil.VALIDATION_DUPLICATION(
                      found,
                      parseInt(getValues('id')),
                      'Duplicated Role Code is Found.'
                    );
                  }
                }
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  disabled={isDefault && isModified}
                  value={field.value ?? ''}
                  error={!!error}
                  helperText={error ? error.message : null}
                  id={'input-role-code'}
                  label="Role Code"
                />
              )}
            />
          </Grid>

          <Grid
            item
            xs={12}
            style={{ marginBottom: '10px', marginTop: '20px' }}>
            <Controller
              name="description"
              control={control}
              rules={{
                required: "Role description can't be empty."
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  disabled={isDefault && isModified}
                  value={field.value ?? ''}
                  error={!!error}
                  helperText={error ? error.message : null}
                  id={'input-role-description'}
                  label="Role Description"
                />
              )}
            />
          </Grid>

          <Grid
            item
            xs={12}
            style={{ marginBottom: '10px', marginTop: '20px' }}>
            <Controller
              name="applicationCode"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  id="input-application"
                  label="Application"
                  value={field.value ?? ''}
                  error={!!error}
                  helperText={error ? error.message : null}
                >
                  {applications &&
                    Array.isArray(applications) &&
                    applications.map((item) => (
                      <MenuItem key={'key-' + uuid()} value={item.code}>
                        {item.description}
                      </MenuItem>
                    ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid
            item
            xs={12}
            style={{ marginBottom: '10px', marginTop: '20px' }}>
            <Controller
              name="permissions"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  multiple
                  fullWidth
                  id="permissions"
                  disabled={watchApplications ? false : true}
                  options={permissionList.filter((x) =>
                    x.code.startsWith(appCodeMapping[watchApplications])
                  )}
                  getOptionLabel={(option) => (option.code ? option.code : '')}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={permissionFilterOptions}
                  onChange={(_, data) => field.onChange(data)}
                  value={getValues('permissions') ?? []}
                  filterSelectedOptions
                  renderOption={(props, { code, description }) => (
                    <li {...props} key={code}>
                      <Tooltip title={description} placement="bottom-start">
                        <TextField
                          InputProps={{
                            readOnly: true,
                            disableUnderline: true
                          }}
                          fullWidth
                          defaultValue={code}
                          variant="standard"
                        />
                      </Tooltip>
                    </li>
                  )}
                  slotProps = {{
                    popper: { placement: 'bottom' },
                      listbox: {
                          sx: {
                            '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ': {
                              color: '#000000 !important'
                            },
                            '& .MuiAutocomplete-option[aria-selected="true"]': {
                              backgroundColor: '#5470b8 !important',
                            },
                            '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                              backgroundColor: '#f4f5f6 !important',
                            }
                          }
                      }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id={'input-permissions'}
                      label="Permissions"
                      placeholder="Chosen Permissions"
                      error={!!error}
                      helperText={error ? error.message : null}
                    />
                  )}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid item xs={6}>
          <List>
            <ListSubheader component="div" id="permission-list-subheader">
              Permission Detail
            </ListSubheader>
            <ul className={classes.ul} type="none">
              {watchPermissions && watchPermissions.length > 0
                ? watchPermissions.map((permission) => (
                  <li key={'key-' + uuid()}>
                    <b>{permission.code}</b> {': ' + permission.description}
                  </li>
                ))
                : 'No permission was found here.'}
            </ul>
          </List>
        </Grid>
      </Grid>
    </>
  );
};

export default RoleManagementDialogDetail;
